<template>
	<div
		:aria-labelledby="groupId"
		class="amelipro-postal-address"
		role="group"
	>
		<p
			:id="groupId"
			class="mb-0 font-weight-semibold"
		>
			{{ groupLabel }}

			<span v-if="ariaRequired">
				<span aria-hidden="true">
					&nbsp;*
				</span>

				<span class="d-sr-only">
					&nbsp;Champs obligatoires
				</span>
			</span>
		</p>

		<!-- first line group -->
		<div class="address-field mt-0 address-field__address">
			<label
				class="text-body-1"
				:for="`${groupId}-address`"
			>
				N° et nom de la voie&nbsp;:&nbsp;

				<span v-if="ariaRequired">
					<span aria-hidden="true">
						*
					</span>

					<span class="d-sr-only">
						&nbsp;Champ obligatoire
					</span>
				</span>
			</label>

			<VTextField
				:id="`${groupId}-address`"
				ref="inputAddressField"
				v-model="internalValue.address"
				:aria-describedby="addressErrorMessage !== '' ? `${groupId}-address-error` : undefined"
				:aria-invalid="addressErrorMessage !== '' ? true : false"
				:aria-required="ariaRequired"
				autocomplete="address-line1"
				:background-color="disabled ? 'ap-grey lighten-2' : 'ap-white'"
				class="pt-0"
				color="ap-blue darken-1"
				dense
				:disabled="disabled"
				hide-details
				outlined
				:readonly="readonly"
				:rules="inputRules"
				@blur="emitEvent('blur')"
				@change="emitEvent('change')"
				@focus="addressFocused = true"
				@update:error="emitEvent('update:error')"
			/>
		</div>

		<AmeliproMessage
			v-if="addressErrorMessage !== ''"
			:id="`${groupId}-address-error`"
			class="mb-0"
			no-icon
			text
			type="error"
		>
			<p class="mb-0">
				{{ addressErrorMessage }}
			</p>
		</AmeliproMessage>

		<!-- second line group -->
		<AmeliproPostalAddressCityRow
			ref="cityRow"
			v-model="internalValue.cityRow"
			:aria-required="ariaRequired"
			:auto-complete-list="autoCompleteList"
			:city-rules="cityRules"
			:disabled="disabled"
			:group-id="groupId"
			:postal-code-rules="postalCodeRules"
			:readonly="readonly"
		/>

		<!-- third line group -->
		<div
			v-if="!noAdditionalInfo"
			class="address-field mt-2 address-field__additional-info"
		>
			<label
				class="text-body-1"
				:for="`${groupId}-additional-info`"
			>
				Complément d'adresse&nbsp;:&nbsp;
			</label>

			<VTextField
				:id="`${groupId}-additional-info`"
				v-model="internalValue.additionalInfo"
				autocomplete="address-line2"
				:background-color="disabled ? 'ap-grey lighten-2' : 'ap-white'"
				class="pt-0"
				color="ap-blue darken-1"
				dense
				:disabled="disabled"
				hide-details
				outlined
				:readonly="readonly"
				@blur="emitEvent('blur')"
				@change="emitEvent('change')"
				@focus="additionalInfoFocused = true"
			/>
		</div>
	</div>
</template>

<script setup lang="ts">
	import { InputPostalAddressAutoCompleteList, InputPostalAddressField, InputPostalAddressGroup } from './types';
	import { PropType, computed, ref, watch } from 'vue';
	import AmeliproMessage from '../AmeliproMessage';
	import AmeliproPostalAddressCityRow from '../AmeliproPostalAddressCityRow';
	import { InputPostalAddressCityRowGroup } from '../AmeliproPostalAddressCityRow/types';
	import { VTextField } from 'vuetify/lib';
	import { ValidationRule } from '../../rules/types';
	import { convertToHex } from '../../functions/convertToHex';
	import { required } from '../../rules/required';

	const props = defineProps({
		addressRules: {
			type: Array as PropType<ValidationRule[]>,
			default: () => [],
		},
		ariaRequired: {
			type: Boolean,
			default: false,
		},
		autoCompleteList: {
			type: Array as PropType<InputPostalAddressAutoCompleteList[]>,
			default: () => [],
		},
		cityRules: {
			type: Array as PropType<ValidationRule[]>,
			default: () => [],
		},
		disabled: {
			type: Boolean,
			default: false,
		},
		groupId: {
			type: String,
			required: true,
		},
		groupLabel: {
			type: String,
			default: 'Adresse postale',
		},
		noAdditionalInfo: {
			type: Boolean,
			default: false,
		},
		postalCodeRules: {
			type: Array as PropType<ValidationRule[]>,
			default: () => [],
		},
		readonly: {
			type: Boolean,
			default: false,
		},
		value: {
			type: Object as PropType<InputPostalAddressGroup>,
			default: () => ({
				additionalInfo: undefined,
				address: undefined,
				cityRow: {
					postalCode: undefined,
					city: undefined,
				},
			}),
		},
	});
	type eventType = 'blur' | 'change' | 'update:error';
	const emit = defineEmits(['blur', 'change', 'input', 'update:error']);

	// v-model
	const internalValue = computed({
		get: (): InputPostalAddressGroup => props.value,
		set: (newValue: InputPostalAddressGroup) => {
			emit('input', newValue);
		},
	});

	const inputAddressField = ref<InputPostalAddressField>({
		hasMessages: false,
		messagesToDisplay: [],
		valid: false,
	});
	const cityRow = ref<InputPostalAddressCityRowGroup>();
	const addressErrorMessages = ref<string[]>([]);
	const addressFocused = ref(false);
	const additionalInfoFocused = ref(false);

	const addressErrorMessage = computed<string>(() => addressErrorMessages.value[0] ?? '');
	const displayAddressError = computed(() => !inputAddressField.value?.valid && inputAddressField.value?.hasMessages);

	const inputRules = computed<ValidationRule[]>(() => {
		const rules = [
			...props.addressRules,
		];

		if (props.ariaRequired) {
			rules.push(required);
		}

		return rules;
	});

	const emitEvent = (eventName: eventType): void => {
		emit(eventName, internalValue.value);
		addressErrorMessages.value = inputAddressField.value.messagesToDisplay as string[] ?? [];
	};

	watch(() => inputAddressField.value.messagesToDisplay, () => {
		addressErrorMessages.value = inputAddressField.value.messagesToDisplay as string[] ?? [];

		if (addressFocused.value) {
			addressFocused.value = false;
		}

		if (additionalInfoFocused.value) {
			additionalInfoFocused.value = false;
		}
	});

	const inputAddressBorderStyle = computed<string>(() => {
		let borderColor = convertToHex('ap-grey darken-1');

		if (addressFocused.value) {
			borderColor = convertToHex('ap-blue darken-1');
		}

		if (displayAddressError.value) {
			borderColor = convertToHex('ap-red');
		}

		if (props.disabled) {
			borderColor = convertToHex('ap-grey lighten-2');
		}

		return `1px solid ${borderColor}`;
	});

	const inputAdditionalInfoBorderStyle = computed<string>(() => {
		let borderColor = convertToHex('ap-grey darken-1');

		if (additionalInfoFocused.value) {
			borderColor = convertToHex('ap-blue darken-1');
		}

		if (props.disabled) {
			borderColor = convertToHex('ap-grey lighten-2');
		}

		return `1px solid ${borderColor}`;
	});
</script>

<style lang="scss" scoped>
.address-field {
	min-width: 200px;
	width: 100%;

	label {
		margin-bottom: 4px;
		font-weight: $label-font-weight;
	}
}

.address-field__address {
	:deep(.v-input__slot) {
		border: v-bind(inputAddressBorderStyle);
	}
}

.address-field__additional-info {
	:deep(.v-input__slot) {
		border: v-bind(inputAdditionalInfoBorderStyle);
	}
}

.address-field__address,
.address-field__additional-info {
	:deep(.v-input__slot) {
		min-height: $input-min-height !important;
		border-radius: $input-radius;

		fieldset {
			border: 0;
		}
	}
}
</style>
